import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDirectusData from '../../hooks/useDirectusData';
import { fetchTestimonials, getImageUrl } from '../../api/directus';
import TestimonialForm from './TestimonialForm';

/**
 * TestimonialsPage component
 * Displays a comprehensive collection of client testimonials
 * Fetches data from Directus CMS
 * Supports multiple languages through i18n
 */
const TestimonialsPage = () => {
  const { t } = useTranslation();
  
  // Fetch testimonials data from Directus CMS
  const { data, loading, error } = useDirectusData(fetchTestimonials);
  
  // Create testimonials array from CMS data or use fallback if loading/error
  const testimonials = data?.data ? 
    data.data
      .filter(item => item.status !== false) // Filter out testimonials with status=false
      .map(item => ({
        quote: item.quote,
        author: item.author,
        company: item.company || "",
        game: item.game || "",
        image: item.image ? getImageUrl(item.image) : 
          `https://via.placeholder.com/600x400/000000/FFFFFF?text=${encodeURIComponent(item.game || item.company || item.author)}`
      })) : 
    [
      {
        quote: "Nathan and Vincent did an excellent job translating Carried Away and its store page. They reacted quickly to modifications and met tight deadlines. We will definitely work with them again!",
        author: "Jonny",
        company: "Huge Calf Studios",
        game: "Carried Away",
        image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/logocarried.png"
      },
      {
        quote: "Very professional team! They helped us translate our game perfectly. Communication was great. Highly recommended!",
        author: "TwoMammoths",
        company: "Developers of Archaica",
        game: "Archaica: The Path of Light",
        image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/header1.jpg"
      },
      {
        quote: "Our game got translated faster than expected! We're extremely happy with the results and excited to introduce our game to a new audience.",
        author: "Christian Wasser",
        company: "Developer of BlobCat",
        game: "BlobCat",
        image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=BlobCat"
      },
      {
        quote: "Quick and high-quality French translation. Our French players now enjoy the game even more!",
        author: "Elementals Reborn Team",
        company: "",
        game: "Elementals Reborn",
        image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=Elementals+Reborn"
      },
      {
        quote: "Vincent has been fantastic at translating Startup Company into French. He's always ready to translate new strings, and I have never received a complaint about the translation from players. Highly recommended!",
        author: "Jonas Hovgaard",
        company: "Developer of Startup Company",
        game: "Startup Company",
        image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/08/logo.png"
      },
      {
        quote: "Best French translators you will ever find. Fast and professional! I've noticed a 21% increase in French players after the translation.",
        author: "Sami",
        company: "Protection Games",
        game: "",
        image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=Protection+Games"
      },
      {
        quote: "The translation quality was exceptional. They captured the humor and tone of our game perfectly in French.",
        author: "Alex Chen",
        company: "Pixel Dreams Studio",
        game: "Moonlight Explorers",
        image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=Moonlight+Explorers"
      },
      {
        quote: "Working with NatuXtranslations was a breeze. They understood our game's unique terminology and maintained consistency throughout.",
        author: "Marie Dubois",
        company: "Stellar Games",
        game: "Cosmic Odyssey",
        image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=Cosmic+Odyssey"
      }
    ];

  // State for form visibility
  const [showForm, setShowForm] = useState(false);
  
  // Handle successful form submission
  const handleFormSuccess = () => {
    // Refresh testimonials data after successful submission
    // Note: New testimonials will be in draft status and won't appear until approved
  };
  
  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Testimonials | NatuXtranslations</title>
        <meta name="description" content="Read what game developers have to say about our French translation and localization services. Discover why indie developers trust us with their games." />
        <meta name="keywords" content="french game translation reviews, game localization testimonials, french translator feedback" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-32 bg-bg-alt relative">
        <div className="container mx-auto px-6">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-normal mb-8 tracking-wide">
              {t('testimonials.clientTestimonials')}
            </h1>
            <p className="text-light text-lg md:text-xl font-light leading-relaxed">
              {t('testimonials.description')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Testimonials Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index}
                testimonial={testimonial}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Submit Testimonial Section */}
      <section className="py-24 bg-white border-t border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
                {t('testimonials.form.title')}
              </h2>
              <p className="text-light text-lg font-light leading-relaxed mb-8">
                {t('testimonials.form.description')}
              </p>
              
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-block bg-primary text-white px-8 py-3 rounded-md transition-all duration-300 hover:bg-primary/90"
                >
                  {t('testimonials.form.shareExperience')}
                </button>
              )}
            </div>
            
            {showForm && (
              <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
                <TestimonialForm onSuccess={handleFormSuccess} />
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-24 bg-bg-alt">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-20">
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
              {t('testimonials.ourImpact')}
            </h2>
            <p className="text-light text-lg font-light leading-relaxed">
              {t('testimonials.impactDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="text-4xl md:text-5xl font-heading text-primary mb-4">25+</div>
              <p className="text-light font-light">{t('testimonials.gamesTranslated')}</p>
            </div>
            
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="text-4xl md:text-5xl font-heading text-primary mb-4">500K+</div>
              <p className="text-light font-light">{t('testimonials.wordsTranslated')}</p>
            </div>
            
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="text-4xl md:text-5xl font-heading text-primary mb-4">15+</div>
              <p className="text-light font-light">{t('testimonials.happyClients')}</p>
            </div>
            
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="text-4xl md:text-5xl font-heading text-primary mb-4">20%</div>
              <p className="text-light font-light">{t('testimonials.avgIncrease')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
            {t('testimonials.joinClients')}
          </h2>
          <p className="text-xl text-white/80 font-light mb-12 max-w-2xl mx-auto">
            {t('testimonials.readyToJoin')}
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-white text-primary px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-white/90 hover:translate-y-[-2px]"
          >
            {t('portfolio.getInTouch')}
          </Link>
        </div>
      </section>
    </>
  );
};

/**
 * TestimonialCard component
 * Displays a single testimonial with styling
 */
const TestimonialCard = ({ testimonial }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: 0.2 });
  
  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <motion.div 
      ref={cardRef}
      className="border border-primary/10 p-8 hover:border-primary transition-all duration-500 h-full flex flex-col"
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      {/* Quote icon */}
      <div className="text-6xl text-primary/5 font-heading mb-4">"</div>
      
      {/* Testimonial content */}
      <div className="flex flex-col flex-grow">
        <div className="mb-8 flex-grow">
          <p className="text-lg text-primary font-light italic leading-relaxed min-h-[120px]">
            {testimonial.quote}
          </p>
        </div>
        
        <div className="flex items-center mt-auto">
          {testimonial.image && (
            <div className="w-16 h-16 mr-4 overflow-hidden border border-primary/10 flex-shrink-0">
              <img 
                src={testimonial.image} 
                alt={testimonial.game || testimonial.company} 
                className="w-full h-full object-contain bg-white"
              />
            </div>
          )}
          
          <div className="flex-grow">
            <p className="font-medium text-primary tracking-wide">
              {testimonial.author}
              {testimonial.company && (
                <span className="text-light font-light ml-1 block sm:inline">— {testimonial.company}</span>
              )}
            </p>
            {testimonial.game && (
              <p className="text-light font-light mt-1">
                {testimonial.game}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TestimonialsPage;
